import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseButton } from '@repo/common';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';

import MetaTag, { buildMetaTags } from '../MetaTag';
import { formatDate } from '../ArticleCard';
import ArticleOptionList from './ArticleOptionList';

const STATUS_BADGE_COLOR = {
  ACTIVE: 'bg-green-light02 text-green-deep04',
  SCHEDULED: 'bg-functional-warning02 text-functional-warning04',
  DRAFT: 'bg-grayscale-gray02 text-grayscale-gray05',
  HIDDEN: 'bg-grayscale-gray05 text-grayscale-gray00',
};

const StatusBadge = ({ statusCode, label }) => (
  <span
    className={`
      inline-flex shrink-0 items-center rounded-lg px-1.5 py-1
      typo-b12Strong h-6 
      ${STATUS_BADGE_COLOR[statusCode] ?? 'bg-grayscale-gray03 text-grayscale-gray08'}
    `}
  >
    {label || statusCode}
  </span>
);

/**
 * 어드민 피드 카드.
 *
 * <p>공개 카드와 달리 상태 배지와 케밥 메뉴(숨기기/삭제)가 붙는다.
 * 본문 편집은 노션에서 하므로 Edit은 없다.
 */
const AdminArticleCard = ({
  article,
  onHide,
  onPublish,
  onDelete,
  isBusy = false,
}) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const optionBtnRef = useRef(null);

  const {
    slug,
    title,
    seoDescription,
    coverImageUrl,
    publishedAt,
    status,
    statusCode,
  } = article;

  const metaTags = buildMetaTags(article, formatDate(publishedAt));
  const goToDetail = () => navigate(`/article/${slug}`);

  return (
    <article
      className='
        group relative flex w-full flex-col overflow-hidden
        rounded-[30px] bg-grayscale-gray00 text-left
        ring-[1px] ring-grayscale-gray02
        transition-[box-shadow,transform] duration-200 ease-out
        motion-reduce:transition-none
        hover:-translate-y-0.5 hover:ring-[6px] hover:ring-surface-green40
      '
    >
      <div
        role='button'
        tabIndex={0}
        onClick={goToDetail}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            goToDetail();
          }
        }}
        className='flex w-full flex-1 cursor-pointer flex-col focus:outline-none'
      >
        <div className='relative h-[240px] w-full shrink-0 overflow-hidden bg-grayscale-gray01'>
          {coverImageUrl ? (
            <img
              src={coverImageUrl}
              alt={title}
              loading='lazy'
              className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]'
            />
          ) : (
            <div className='flex h-full w-full items-center justify-center typo-b13 text-text-placeholder'>
              Koready
            </div>
          )}

          {/*
            어드민 카드에만 딤을 얹는다. 밝은 커버 위에서도 배지·옵션 아이콘이 읽히게 하려는 것.
            디자인 토큰 surface-black40은 실제 값이 0.64라 시안(0.40)과 달라 직접 지정한다.
          */}
          <div
            aria-hidden
            className='pointer-events-none absolute inset-0 bg-[rgba(23,20,16,0.40)]'
          />
        </div>

        <div className='flex w-full flex-1 flex-col p-4'>
          <div className='flex flex-1 flex-col gap-1 pb-2 tabletH:gap-1.5 tabletH:pb-4'>
            <h3 className='typo-title3 tabletV:typo-title2 text-grayscale-gray09 line-clamp-2'>
              {title}
            </h3>

            {seoDescription && (
              <p className='typo-krCaption tabletV:typo-b13Strong text-text-secondary line-clamp-2'>
                {seoDescription}
              </p>
            )}
          </div>

          {metaTags.length > 0 && (
            <div className='mt-auto flex flex-wrap items-center gap-1.5'>
              {metaTags.map((tag, index) => (
                <MetaTag key={`${tag}-${index}`}>{tag}</MetaTag>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className='absolute right-2 top-3 flex items-center gap-1'>
        {(statusCode || status) && (
          <StatusBadge statusCode={statusCode} label={statusCode || status} />
        )}

        <BaseButton
          ref={optionBtnRef}
          type='text'
          size='m'
          icon={<OptionIcon />}
          aria-label='Article actions'
          isDisabled={isBusy}
          /*
           * type='text'의 기본 active가 bg-green-light01이라 명시적으로 꺼야 한다.
           * bg-none은 background-image라서 배경색을 못 지운다 — bg-transparent를 써야 함.
           */
          bgclass='bg-transparent border-none rounded-full'
          textclass={isMenuOpen ? 'text-icon-disable' : 'text-icon-inverse'}
          hoverclass='hover:bg-transparent hover:text-icon-disable'
          activeclass='active:bg-transparent active:text-icon-disable'
          onClick={(event) => {
            event.stopPropagation();
            setIsMenuOpen((prev) => !prev);
          }}
        />
      </div>

      {isMenuOpen && optionBtnRef.current && (
        <ArticleOptionList
          anchorRect={optionBtnRef.current.getBoundingClientRect()}
          statusCode={statusCode}
          onClose={() => setIsMenuOpen(false)}
          onHide={() => onHide(article)}
          onPublish={() => onPublish(article)}
          onDelete={() => onDelete(article)}
        />
      )}
    </article>
  );
};

export default AdminArticleCard;
